import { Button, Icon } from "@material-ui/core";
import {
  ResolutionType,
  useScreenStore,
} from "application/contexts/screen/ScreenStore";
import { useUi } from "application/contexts/ui/UIContext";
import { ICartContext } from "modules/cart/domain/interfaces/IUseCaseCart";
import { useCartStore } from "modules/cart/infra/store/CartStore";
import React, { FC, memo, useCallback, useRef, useState } from "react";
import LazyLoad from "react-lazyload";
import { Product } from "../../../../domains/catalog/aggregates/Product";
import styles from "./ProductItem.module.scss";
import { CSSProperties } from "react";
import { useKiosk } from "application/contexts/kiosk/KioskContext";
import { hexToRGBA, moneyMask } from "utils/utils";
import AddButton from "./components/AddButton";
import BottomModal from "components/bottomModal/BottomModal";
import { ProdutoTipo } from "Infrastructure/interfaces/PedidoMeep";
import FoodCategories from "../foodCategories/FoodCategories";
import { useTranslation } from "i18n/useTranslation";

interface ProductItemProps {
  product: Product;
  type?: number;
  hidePrice?: boolean;
  openDetail: (
    productId: string,
    refItem: React.RefObject<HTMLDivElement>
  ) => void;
  isClosed?: boolean;
  hideCategory: boolean;
}

export const ProductItem: FC<ProductItemProps> = memo((props) => {
  const [addProductCart, removeProductCart, getProductQuantity] = useCartStore(
    (state: ICartContext) => [
      state.addProductCart,
      state.removeCartItemByProductId,
      state.getProductQuantity,
    ]
  );
  const { resolution } = useScreenStore();
  const { kiosk } = useKiosk();
  const { getProductName } = useTranslation();

  const refContainer = useRef<HTMLDivElement>(null);

  const { toast } = useUi();
  const [showImageModal, setShowImageModal] = useState(false);

  const hasComposition = useCallback((product?: Product) => {
    if (product && product.compositions?.length) {
      if (product.compositions.some((it) => it.compositionItems.length > 0)) {
        return true;
      } else {
        return false;
      }
    } else if (
      product?.cartRecommendationByProduct?.length &&
      product.cartRecommendationByProduct.length > 0
    ) {
      return true;
    } else {
      return false;
    }
  }, []);

  const openDetail = useCallback(() => {
    props.openDetail(props.product.id, refContainer);
  }, [props]);

  const onClickAdd = useCallback(
    (event: React.MouseEvent<HTMLImageElement>) => {
      event.stopPropagation();
      if (props.isClosed) {
        toast("Loja fechada", "error");
      } else {
        if (
          hasComposition(props.product) ||
          props.product?.productGeneralObservation ||
          props.product.type === ProdutoTipo.Kilo
        ) {
          openDetail();
        } else {
          addProductCart(props.product, 1, [], props.product.adultHood);
        }
      }
    },
    [
      addProductCart,
      hasComposition,
      openDetail,
      props.isClosed,
      props.product,
      toast,
    ]
  );

  const onClickAddFromModal = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      if (props.isClosed) {
        toast("Loja fechada", "error");
      } else {
        if (
          hasComposition(props.product) ||
          props.product?.productGeneralObservation
        ) {
          openDetail();
        } else {
          addProductCart(props.product, 1, [], props.product.adultHood);
        }
      }
      setShowImageModal(false);
    },
    [
      addProductCart,
      hasComposition,
      openDetail,
      props.isClosed,
      props.product,
      toast,
    ]
  );

  const onClickImage = useCallback(
    (event: React.MouseEvent<HTMLImageElement>) => {
      event.stopPropagation();
      setShowImageModal(true);
    },
    []
  );

  const handleAddButton = useCallback(
    (qtd: number) => {
      if (props.isClosed) {
        toast("Loja fechada", "error");
      } else {
        if (
          hasComposition(props.product) ||
          props.product?.productGeneralObservation
        ) {
          openDetail();
        } else {
          addProductCart(props.product, qtd, [], props.product.adultHood);
        }
      }
    },
    [
      addProductCart,
      hasComposition,
      openDetail,
      props.isClosed,
      props.product,
      toast,
    ]
  );

  const onClickRemove = useCallback(() => {
    removeProductCart(props.product.id, 1);
  }, [props.product, removeProductCart]);

  const onImgError = (ev: React.ChangeEvent<HTMLImageElement>) => {
    ev.target.src = require("../../../../assets/images/no-image.png");
  };

  const quantity = getProductQuantity(props.product.id);

  return (
    <LazyLoad
      placeholder={
        <div
          style={{
            width: resolution === ResolutionType.FULL_HD ? 260 : 160,
            height: resolution === ResolutionType.FULL_HD ? 380 : 250,
            padding: 12,
            border: "4px solid transparent",
            boxSizing: "content-box",
            flexShrink: 0,
          }}
        />
      }      
      offset={200}
      once
      scrollContainer="#product-scroll-container"
    >
      <div
        ref={refContainer}
        id={styles.ProductItem}
        style={
          {
            width: resolution === ResolutionType.FULL_HD ? 260 : 160,
            height: resolution === ResolutionType.FULL_HD ? 380 : 250,
            "--border-color":
              quantity > 0
                ? kiosk?.themeColor ?? kiosk?.primaryColor
                : "#fbfbf9",
          } as CSSProperties
        }
      >
        <div className={styles.foodCategoryTag} onClick={onClickImage}>
          {props.product.foodCategories.map((fc) => (
            <FoodCategories
              key={fc.id}
              id={fc?.id ?? ""}
              size={resolution === ResolutionType.FULL_HD ? 40 : 20}
            />
          ))}
        </div>
        <div className={styles.iconInfo}>
          <Icon onClick={onClickImage}>info_outline</Icon>
        </div>
        {!props.type && (
          <img
            className={styles.image}
            style={{
              width: resolution === ResolutionType.FULL_HD ? 175 : 110,
              height: resolution === ResolutionType.FULL_HD ? 175 : 110,
            }}
            onClick={onClickAdd}
            onErrorCapture={onImgError}
            src={props.product.thumbnailUrl}
            alt="product"
          />
        )}
        {props.product?.discount && (
          <div onClick={onClickAdd} className="produto-item-desconto">
            -{props.product.discount}%
          </div>
        )}

        <div
          //onClick={onClickAdd}
          className={styles.info}
          style={{
            minHeight: resolution === ResolutionType.FULL_HD ? 140 : 95,
          }}
        >
          <div
            onClick={onClickAdd}
            style={{
              maxWidth: resolution === ResolutionType.FULL_HD ? 250 : 170,
              fontSize: resolution === ResolutionType.FULL_HD ? 22 : 16,
              margin:
                resolution === ResolutionType.FULL_HD
                  ? "16px 0"
                  : "8px 0 6px 0",
            }}
          >
            {getProductName(props.product)}
          </div>
          <div
            onClick={onClickAdd}
          >
            <span
              className={
                props.product.discount ? styles.discountValue : undefined
              }
            >
              {props.product.price > 0 ? (
                <>
                  <span
                    style={{
                      fontSize: resolution === ResolutionType.FULL_HD ? 28 : 20,
                    }}
                  >
                    R$
                  </span>
                  <span
                    style={{
                      fontSize: resolution === ResolutionType.FULL_HD ? 42 : 32,
                    }}
                  >
                    {
                      props.product.price
                        .toFixed(2)
                        .replace(".", ",")
                        .split(",")[0]
                    }
                  </span>
                  <span
                    style={{
                      fontSize: resolution === ResolutionType.FULL_HD ? 28 : 20,
                    }}
                  >
                    ,{moneyMask(props.product.price).split(",")[1]}
                  </span>
                  {props.product.type === ProdutoTipo.Kilo && (
                    <span
                      style={{
                        fontSize:
                          resolution === ResolutionType.FULL_HD ? 16 : 10,
                        fontWeight: 'normal'
                      }}
                    >
                      /Kg
                    </span>
                  )}
                </>
              ) : (
                <></>
              )}
            </span>
            {props.product.discount && (
              <span style={{ marginLeft: -30 }}>
                {moneyMask(
                  props.product.price -
                    props.product.price * (props.product.discount / 100)
                )}
              </span>
            )}
          </div>
        </div>
        <div
          className={styles.quantitySelector}
          style={{
            height: resolution === ResolutionType.FULL_HD ? 60 : 50,
            marginTop: resolution === ResolutionType.FULL_HD ? 16 : 8,
          }}
        >
          {props.product.type !== ProdutoTipo.Kilo &&
            quantity > 0 && (
              <div
                style={
                  {
                    "--color": hexToRGBA(
                      kiosk?.themeColor ?? kiosk?.primaryColor ?? "#001537",
                      0.1
                    ),
                  } as CSSProperties
                }
              >
                <Icon onClick={onClickRemove}>remove</Icon>
                <span>{quantity}</span>
                <AddButton handleAddButton={handleAddButton} />
              </div>
            )}
        </div>
        <BottomModal
          showModal={showImageModal}
          onClickClose={() => setShowImageModal(false)}
        >
          <div className={styles.imageModal}>
            <div>
              <span
                style={{
                  maxWidth: resolution === ResolutionType.FULL_HD ? 800 : 500,
                }}
              >
                {getProductName(props.product)}
              </span>
              <div className={styles.foodCategoryDetailTag}>
                {props.product.foodCategories.map((fc) => (
                  <FoodCategories
                    key={fc.id}
                    id={fc?.id ?? ""}
                    size={resolution === ResolutionType.FULL_HD ? 80 : 60}
                  />
                ))}
              </div>
              <img
                className={styles.image}
                style={{
                  width: resolution === ResolutionType.FULL_HD ? 800 : 500,
                  height: resolution === ResolutionType.FULL_HD ? 500 : 350,
                }}
                onErrorCapture={onImgError}
                src={props.product.thumbnailUrl}
                alt="product"
              />
            </div>
            <p
              style={{
                fontSize: resolution === ResolutionType.FULL_HD ? 26 : 18,
                margin:
                  resolution === ResolutionType.FULL_HD ? "64px 0" : "32px 0",
                maxHeight: resolution === ResolutionType.FULL_HD ? 525 : 408,
              }}
            >
              {props.product.description}
            </p>
            <Button
              fullWidth
              variant="contained"
              style={{
                fontSize: resolution === ResolutionType.FULL_HD ? 45 : 32,
                padding: resolution === ResolutionType.FULL_HD ? 32 : 22,
              }}
              onClick={onClickAddFromModal}
            >
              <span>{moneyMask(props.product.price)}</span>
              <span>Adicionar</span>
            </Button>
          </div>
        </BottomModal>
      </div>
    </LazyLoad>
  );
});
